import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Alert, Button, CircularProgress, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import moment from "moment";
import { BASE_URL } from "../../../config/config";

const money = (n) => `Rs ${Number(n || 0).toLocaleString()}`;

// Cash bookings only: the state of the OTS service fee the tenant paid to
// OTS's own account. The owner is paid in cash directly; this fee is a separate
// receipt that an OTS admin reviews, and the tenancy cannot start until it is
// verified. Nothing renders for online payments or for a 0% fee.
export default function OtsFeePanel({ request, onChanged }) {
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const fee = request?.otsFee;
  if (!fee?.required || request?.moveInStatus === "cancelled") return null;
  if (request?.moveInStatus === "moved_in") return null;

  const requestId = request._id || request.id;

  const resubmit = async () => {
    if (!file) return;
    setSending(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("otsFeeImage", file);
      const res = await axios.post(
        `${BASE_URL}/flat-interest/resubmit-ots-fee/${requestId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (res?.data?.success) {
        toast.success(res.data.message || "Receipt sent");
        setFile(null);
        onChanged?.();
      } else {
        setError("Could not send the receipt. Please try again.");
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || "Could not send the receipt. Please try again."
      );
    } finally {
      setSending(false);
    }
  };

  if (fee.status === "verified") {
    return (
      <Alert severity="success" sx={{ borderRadius: 2, fontSize: "0.85rem", mb: 2 }}>
        OTS has verified your service fee ({money(fee.amount)}).
      </Alert>
    );
  }

  if (fee.status === "rejected") {
    const deadline = fee.resubmitDeadline ? moment(fee.resubmitDeadline) : null;
    return (
      <Alert severity="error" sx={{ borderRadius: 2, fontSize: "0.85rem", mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          OTS could not verify your service fee receipt ({money(fee.amount)})
        </Typography>
        {fee.rejectionReason && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Reason: {fee.rejectionReason}
          </Typography>
        )}
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Please upload a correct receipt
          {deadline ? ` by ${deadline.format("DD MMM YYYY, h:mm A")}` : ""}, or the
          booking will be cancelled.
        </Typography>

        <Button
          component="label"
          size="small"
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          sx={{ mt: 1.2, mr: 1, textTransform: "none", fontWeight: 600 }}
        >
          {file ? file.name : "Choose receipt image"}
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={!file || sending}
          onClick={resubmit}
          startIcon={sending ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ mt: 1.2, textTransform: "none", fontWeight: 700 }}
        >
          {sending ? "Sending…" : "Send receipt"}
        </Button>

        {error && (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
            {error}
          </Typography>
        )}
      </Alert>
    );
  }

  // pending
  return (
    <Alert severity="warning" sx={{ borderRadius: 2, fontSize: "0.85rem", mb: 2 }}>
      OTS is checking your service fee receipt ({money(fee.amount)}). Your tenancy
      can start once it is verified.
    </Alert>
  );
}
